import React from "react";
import { DatePicker as MuiDatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Typography } from "@mui/material";
import dayjs from "dayjs";
import "./style.css";

const DatePicker = React.forwardRef(
  (
    {
      name,
      label,
      value,
      placeholder,
      onChange,
      onBlur,
      error,
      errorBorderOnly = false,
      helperText,
      disabled = false,
      format = "MM/DD/YYYY",
      className = "",
      minDate = null,
      maxDate = null,
      required = false,
      fieldHeight,
      popperProps,
      shouldDisableDate = null,
      slotProps: externalSlotProps,
      onAccept: externalOnAccept,
      sx: sxProp,
      ...props
    },
    ref
  ) => {
    // Convert value to dayjs object if it's a valid date string
    const dayjsValue = React.useMemo(() => {
      if (!value) return null;
      if (dayjs.isDayjs(value)) return value;
      if (typeof value === "string") {
        // Try parsing with the specified format first (strict mode)
        const parsedWithFormat = dayjs(value, format, true);
        if (parsedWithFormat.isValid()) {
          return parsedWithFormat;
        }
        // Fallback to default parsing for YYYY-MM-DD format (from API)
        const parsed = dayjs(value);
        if (parsed.isValid()) {
          return parsed;
        }
      }
      return null;
    }, [value, format]);

    // Helper to validate if a parsed date matches the expected format strictly
    const isValidCompleteDate = React.useCallback((dateValue, dateFormat) => {
      if (!dateValue || !dayjs.isDayjs(dateValue) || !dateValue.isValid()) {
        return false;
      }
      
      // Format the date using the expected format
      const formatted = dateValue.format(dateFormat);
      
      // Parse it back with strict mode to ensure it matches the format exactly
      const reparsed = dayjs(formatted, dateFormat, true);
      
      // Check if reparsed date is valid and matches the original
      if (!reparsed.isValid() || reparsed.format(dateFormat) !== formatted) {
        return false;
      }
      
      // If format requires 4-digit year (YYYY), ensure the year is 4 digits
      // This prevents accepting dates parsed from 2-digit years like "01-08-20"
      if (dateFormat.includes('YYYY')) {
        const year = dateValue.year();
        // Check that year is reasonable (4-digit years)
        // Years like 20, 26, etc. when parsed become 2020, 2026, but we want to
        // ensure the original input had 4 digits, not 2
        // Since we can't check the original input, we check if the formatted year
        // position matches what we expect
        const yearFormatted = dateValue.format('YYYY');
        if (yearFormatted.length !== 4 || year < 1900 || year > 2100) {
          return false;
        }
      }
      
      return true;
    }, []);

    // Handle date change - fires on every keystroke and calendar selection
    // Use YYYY-MM-DD format to preserve local date without timezone conversion
    const handleChange = (newValue) => {
      // For calendar selections, always accept (they're always complete)
      // For manual input, validate that the date is complete and matches format
      if (newValue && dayjs.isDayjs(newValue) && newValue.isValid()) {
        // Validate that the date is complete and matches the expected format
        // This prevents accepting incomplete dates like "01-08-20" when format is "MM/DD/YYYY"
        if (isValidCompleteDate(newValue, format)) {
          // Format as YYYY-MM-DD to preserve the selected date without timezone issues
          const dateString = newValue.format('YYYY-MM-DD');
          onChange({ target: { name, value: dateString } });
        }
        // If validation fails, don't update state (user is still typing or date is incomplete)
      } else if (newValue === null) {
        // Clear the value
        onChange({ target: { name, value: null } });
      }
      // For invalid/incomplete input during typing, don't update state
      // This prevents premature parsing of incomplete dates
    };

    // Handle date acceptance - fires when a valid date is fully entered/accepted
    // This is the key handler for manual input - only fires when date is complete
    const handleAccept = (acceptedValue) => {
      if (acceptedValue && dayjs.isDayjs(acceptedValue) && acceptedValue.isValid()) {
        // Format as YYYY-MM-DD to preserve the selected date without timezone issues
        const dateString = acceptedValue.format('YYYY-MM-DD');
        onChange({ target: { name, value: dateString } });
      }
      
      // Call external onAccept if provided
      if (externalOnAccept) {
        externalOnAccept(acceptedValue);
      }
    };

    /** Match InputText / InputFields.css — soft gray border, blue focus ring (no navy outline) */
    const textFieldOutlineSx = React.useMemo(
      () => ({
        "& .MuiOutlinedInput-root": {
          borderRadius: "8px",
          minHeight: fieldHeight || "44px",
          height: fieldHeight || "44px",
          backgroundColor: "#F7F8FA",
          transition:
            "background-color 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease",
          "&.Mui-focused:not(.Mui-disabled):not(.Mui-error)": {
            backgroundColor: "#FFFFFF",
            boxShadow: "var(--focus-ring)",
          },
          "&.Mui-disabled": {
            backgroundColor: "#F7F8FA",
            opacity: 1,
          },
          "&.Mui-disabled .MuiOutlinedInput-input": {
            WebkitTextFillColor: "#4b5563",
            color: "#4b5563",
          },
          "&.Mui-disabled .MuiOutlinedInput-input::placeholder": {
            WebkitTextFillColor: "#9ca3af",
            opacity: 1,
          },
          "&.Mui-disabled .MuiSvgIcon-root": {
            color: "#64748b",
            opacity: 1,
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: `${error ? "#dc3545" : "#E5E7EB"} !important`,
            borderWidth: "1px !important",
            borderStyle: "solid !important",
          },
          "&:hover:not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
            borderColor: `${error ? "#dc3545" : "#D1D5DB"} !important`,
          },
          "&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
            borderColor: `${error ? "#dc3545" : "#2563EB"} !important`,
            borderWidth: "1px !important",
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: "#dc3545 !important",
          },
          "&.Mui-error:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#dc3545 !important",
          },
          "&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#dc3545 !important",
            boxShadow: "var(--focus-ring-error)",
          },
        },
      }),
      [error, fieldHeight]
    );

    // Merge slotProps to support popper configuration
    const mergedSlotProps = React.useMemo(() => {
      // MUI TextField shows placeholder only when value is empty; use `format` as hint when prop omitted
      const textFieldPlaceholder =
        placeholder !== undefined && placeholder !== null ? placeholder : format;

      const mergeTextFieldSx = (extTf = {}) => {
        const extra = extTf.sx
          ? Array.isArray(extTf.sx)
            ? extTf.sx
            : [extTf.sx]
          : [];
        return [textFieldOutlineSx, ...extra];
      };

      const baseTextField = {
        placeholder: textFieldPlaceholder,
        inputProps: {
          "aria-label": name,
          placeholder: textFieldPlaceholder,
        },
        required: required,
        helperText: helperText,
        error: !!error,
        inputRef: ref,
        sx: textFieldOutlineSx,
      };

      const baseSlotProps = {
        textField: baseTextField,
        // Mobile calendar dialog configuration - show selected date in toolbar
        mobilePaper: {
          sx: {
            "& .MuiPickersToolbar-root": {
              display: "flex",
              padding: "16px",
              "& .MuiPickersToolbar-content": {
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              },
              "& .MuiPickersToolbar-text": {
                fontSize: "14px !important",
                fontWeight: "400 !important",
                color: "#0f2b46 !important",
              },
              "& .MuiTypography-root": {
                fontSize: "14px !important",
                fontWeight: "400 !important",
                color: "#0f2b46 !important",
              },
              "& .MuiTypography-h4": {
                fontSize: "14px !important",
                fontWeight: "400 !important",
                color: "#0f2b46 !important",
              },
            },
          },
        },
        // Configure toolbar to display formatted date with custom styling
        toolbar: {
          hidden: false,
          toolbarFormat: format,
          toolbarPlaceholder: format,
          sx: {
            "& .MuiPickersToolbar-text": {
              fontSize: "14px !important",
              fontWeight: "400 !important",
              color: "#0f2b46 !important",
            },
            "& .MuiTypography-root": {
              fontSize: "14px !important",
              fontWeight: "400 !important",
              color: "#0f2b46 !important",
            },
            "& .MuiTypography-h4": {
              fontSize: "14px !important",
              fontWeight: "400 !important",
              color: "#0f2b46 !important",
            },
          },
        },
        calendarHeader: {
          format: "MMMM YYYY",
          disableYearSelector: true,
        },
      };

      // Merge popper props if provided
      if (popperProps) {
        baseSlotProps.popper = popperProps;
      }

      // Merge with any slotProps from props (always re-apply placeholder so external `sx` does not drop it)
      if (externalSlotProps) {
        const extTf = externalSlotProps.textField || {};
        return {
          ...baseSlotProps,
          ...externalSlotProps,
          textField: {
            ...baseTextField,
            ...extTf,
            placeholder: textFieldPlaceholder,
            inputProps: {
              ...baseTextField.inputProps,
              ...(extTf.inputProps || {}),
              placeholder: textFieldPlaceholder,
            },
            sx: mergeTextFieldSx(extTf),
          },
          mobilePaper: {
            ...baseSlotProps.mobilePaper,
            ...(externalSlotProps.mobilePaper || {}),
            sx: {
              ...(baseSlotProps.mobilePaper?.sx || {}),
              ...(externalSlotProps.mobilePaper?.sx || {}),
            },
          },
          toolbar: {
            ...baseSlotProps.toolbar,
            ...(externalSlotProps.toolbar || {}),
          },
          popper: {
            ...(baseSlotProps.popper || {}),
            ...(externalSlotProps.popper || {}),
          },
        };
      }

      return baseSlotProps;
    }, [
      placeholder,
      required,
      helperText,
      error,
      ref,
      popperProps,
      externalSlotProps,
      format,
      name,
      textFieldOutlineSx,
    ]);

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DemoContainer 
          components={["DatePicker"]}
          sx={{
            paddingTop: label ? undefined : "0px !important",
            margin: "0 !important",
            overflow: "visible !important", // Prevent label clipping
            "& .MuiStack-root": {
              paddingTop: label ? undefined : "0px !important",
              margin: "0 !important",
              overflow: "visible !important", // Prevent label clipping
            },
          }}
        >
          <MuiDatePicker
            id={name}
            name={name}
            className={`date-picker-component ${className}`}
            {...(label != null && label !== "" ? { label } : {})}
            disabled={disabled}
            value={dayjsValue}
            onChange={handleChange}
            onAccept={handleAccept}
            onBlur={onBlur}
            format={format}
            minDate={minDate ? dayjs(minDate) : undefined}
            maxDate={maxDate ? dayjs(maxDate) : undefined}
            shouldDisableDate={shouldDisableDate}
            views={['year', 'month', 'day']}
            openTo="day"
            slotProps={mergedSlotProps}
            sx={[
              {
                width: "100%",
                minWidth: "250px",
                margin: "0 !important",
                "& .MuiPickersCalendarHeader-switchViewButton": {
                  display: "none !important",
                },
                "& .MuiPickersArrowSwitcher-root": {
                  "& .MuiIconButton-root[aria-label*='year']": {
                    display: "none !important",
                  },
                },
                "& .MuiPickersCalendarHeader-labelContainer": {
                  "& button": {
                    display: "none !important",
                  },
                },
                /* Match InputText (soft border + blue focus ring) */
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  margin: "0 !important",
                  backgroundColor: "#F7F8FA",
                  transition:
                    "background-color 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease",
                  "&.Mui-focused:not(.Mui-disabled):not(.Mui-error)": {
                    backgroundColor: "#FFFFFF",
                    boxShadow: "var(--focus-ring)",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#F7F8FA",
                    opacity: 1,
                  },
                  "&.Mui-disabled .MuiOutlinedInput-input": {
                    WebkitTextFillColor: "#4b5563 !important",
                    color: "#4b5563",
                  },
                  "&.Mui-disabled .MuiOutlinedInput-input::placeholder": {
                    WebkitTextFillColor: "#9ca3af !important",
                    opacity: 1,
                  },
                  "&.Mui-disabled .MuiSvgIcon-root": {
                    color: "#64748b !important",
                    opacity: 1,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: `${error ? "#dc3545" : "#E5E7EB"} !important`,
                    borderWidth: "1px !important",
                    borderStyle: "solid !important",
                  },
                  "&:hover:not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
                    borderColor: `${error ? "#dc3545" : "#D1D5DB"} !important`,
                  },
                  "&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
                    borderColor: `${error ? "#dc3545" : "#2563EB"} !important`,
                    borderWidth: "1px !important",
                  },
                  "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#dc3545 !important",
                  },
                  "&.Mui-error:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#dc3545 !important",
                  },
                  "&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#dc3545 !important",
                    boxShadow: "var(--focus-ring-error)",
                  },
                  "& .MuiOutlinedInput-notchedOutline legend": {
                    maxWidth: "100% !important",
                  },
                },
                "& .MuiInputBase-input": {
                  color: error && !errorBorderOnly ? "#dc3545 !important" : "#4b5563",
                },
                "& .MuiInputBase-input::placeholder": {
                  color:
                    error && !errorBorderOnly
                      ? "#dc3545 !important"
                      : "#9ca3af !important",
                  opacity: 1,
                },
                "& .MuiInputLabel-root": {
                  color: error && !errorBorderOnly ? "#dc3545 !important" : "#64748b",
                  fontSize: "15px",
                  display: label ? undefined : "none",
                  "&.MuiInputLabel-shrink, &.Mui-focused, &.MuiFormLabel-filled": {
                    backgroundColor: "#ffffff !important",
                    padding: "0 6px !important",
                    maxWidth: "calc(100% - 24px) !important",
                    zIndex: 2,
                    transform: "translate(14px, -9px) scale(0.75) !important",
                  },
                },
                "& .MuiInputLabel-root.Mui-error": {
                  color: !errorBorderOnly ? "#dc3545 !important" : undefined,
                },
                "& .MuiSvgIcon-root": {
                  color: error && !errorBorderOnly ? "#dc3545 !important" : "#64748b",
                },
                "& .MuiFormHelperText-root": {
                  fontSize: "12px",
                  color: "#d32f2f",
                  marginLeft: "0px",
                  paddingLeft: "0px",
                },
              },
              ...(sxProp
                ? Array.isArray(sxProp)
                  ? sxProp
                  : [sxProp]
                : []),
            ]}
            {...props}
          />
        </DemoContainer>
        {/* {error && (
          <Typography
            className="date-picker-error"
            style={{
              color: "red",
              marginLeft: "10px",
              fontSize: "13px",
            }}
          >
            {error}
          </Typography>
        )} */}
      </LocalizationProvider>
    );
  }
);

export default DatePicker;
